import React from 'react';
import { cn } from '@/ui';

interface AIDraftBadgeProps {
  /** When true, render only the sparkle glyph (used by ranked chips in
   *  SuggestionsStrip — same atom, no "draft" label since chips aren't drafts). */
  glyphOnly?: boolean;
  className?: string;
}

/**
 * The shared "AI-touched" cue. Per design:
 * - Identical sparkle glyph + brand-ink color across all AI surfaces
 * - Surfaces with draft state (FindingCard) carry the full "AI draft" label
 * - Ranking-only surfaces (SuggestionsStrip chips) use glyphOnly
 */
const AIDraftBadge: React.FC<AIDraftBadgeProps> = ({ glyphOnly = false, className }) => {
  // The brand-ink Tailwind token is WHITE (intended for text on a solid-brand
  // bg, e.g. primary button labels). On a light tint it would be invisible.
  // The badge needs a deep-brand foreground; we use the brand-700 hex from
  // index.css directly so contrast is ~8.5:1 against bg-brand-soft (AAA).
  if (glyphOnly) {
    return (
      <span
        className={cn('inline-flex items-center justify-center text-[#1A4328]', className)}
        aria-hidden
      >
        <Sparkle size={11} />
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border border-brand/30 bg-brand-soft',
        'px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#1A4328]',
        'font-mono leading-none',
        className,
      )}
      title="AI-generated draft — visible only to you"
    >
      <Sparkle size={9} />
      <span>AI draft</span>
    </span>
  );
};

function Sparkle({ size = 9 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 12 12"
      fill="currentColor"
      className="shrink-0"
      aria-hidden
    >
      <path d="M6 0 L7 4.2 L11 5 L7 5.8 L6 10 L5 5.8 L1 5 L5 4.2 Z" />
    </svg>
  );
}

export default AIDraftBadge;
